import { forwardRef, ForwardedRef } from "react";
import {
  MediaController,
  MediaControlBar,
  MediaTimeRange,
  MediaTimeDisplay,
  MediaFullscreenButton,
  MediaPipButton,
} from "media-chrome/react";
import {
  MediaProvider,
  useMediaRef,
} from "media-chrome/dist/react/media-store";
import { useTranslation } from "i18n";
import cx from "classnames";
import { MediaType } from "shared-types";
import { PipIcon, FullScreenPlayerIcon, ExternalLinkIcon } from "assets";
import PlayButton from "pages/Profile/components/common/MediaPlayer/components/PlayButton";
import VolumeControl from "pages/Profile/components/common/MediaPlayer/components/VolumeControl";
import FadePlayButton from "./components/FadePlayButton";
import "./styles.scss";

interface PlayerProps {
  src: string;
  title?: string;
  poster?: string;
  type?: MediaType;
  onPlay?: () => void;
  externalLink?: string;
}

interface PlayerContentProps extends PlayerProps {
  videoRef: ForwardedRef<HTMLVideoElement>;
}

const PlayerContent = ({
  title,
  src,
  poster,
  type,
  onPlay,
  externalLink,
  videoRef,
}: PlayerContentProps) => {
  const { t } = useTranslation();
  const mediaRef = useMediaRef();

  const handleVideoRef = (node: HTMLVideoElement | null) => {
    mediaRef(node);

    if (typeof videoRef === "function") {
      videoRef(node);

      return;
    }

    if (videoRef) {
      videoRef.current = node;
    }
  };

  return (
    <div>
      <MediaController
        style={{
          width: "100%",
          height: "100%",
          aspectRatio: "16/9",
          borderRadius: 16,
        }}
        className="media-controller"
        autohideOverControls
      >
        {/* eslint-disable-next-line jsx-a11y/media-has-caption */}
        <video
          slot="media"
          src={src}
          preload="auto"
          ref={handleVideoRef}
          poster={poster}
          onPlay={onPlay}
        />

        {type === MediaType.Audio && (
          <div className="h-full w-full bg-[linear-gradient(180deg,#2741E8_0%,#20016B_100%)]" />
        )}

        <FadePlayButton />

        <div
          className={cx(
            "control-bar absolute bottom-0 left-0 mt-1 flex w-full flex-col gap-2",
            "bg-[linear-gradient(0deg,rgba(0,0,0,0.8)_0%,rgba(0,0,0,0.6)_50%,rgba(0,0,0,0)_100%)]"
          )}
        >
          <MediaTimeRange className="time-range" />

          <MediaControlBar className="h-9 w-full items-center gap-3 bg-transparent px-4 py-1">
            <PlayButton />

            <MediaTimeDisplay
              showDuration
              noToggle
              className="ml-2 bg-transparent p-0 text-sm text-white"
            />

            <VolumeControl />

            <div className="ml-auto min-w-0 flex-1 truncate text-right text-sm font-semibold text-white">
              {title}
            </div>
            {externalLink && (
              <a
                href={externalLink}
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => {
                  if (
                    typeof videoRef === "object" &&
                    videoRef !== null &&
                    "current" in videoRef
                  ) {
                    videoRef?.current?.pause();
                  }
                }}
                aria-label={t("common:aria.label.openMediaInNewTab")}
              >
                <ExternalLinkIcon aria-hidden="true" />
              </a>
            )}
            <MediaPipButton className="bg-transparent p-0">
              <span slot="enter">
                <PipIcon width={20} height={20} />
              </span>
              <span slot="exit">
                <PipIcon width={20} height={20} />
              </span>
            </MediaPipButton>
            <MediaFullscreenButton className="bg-transparent p-0">
              <span slot="enter">
                <FullScreenPlayerIcon width={20} height={20} />
              </span>
              <span slot="exit">
                <FullScreenPlayerIcon width={20} height={20} />
              </span>
            </MediaFullscreenButton>
          </MediaControlBar>
        </div>
      </MediaController>
    </div>
  );
};

const Player = forwardRef<HTMLVideoElement, PlayerProps>((props, ref) => {
  return (
    <MediaProvider>
      <PlayerContent {...props} videoRef={ref} />
    </MediaProvider>
  );
});

export default Player;
